/**
 * WT1 - the Walk-throughs list, `/tours` (plan section E), in the nav as "Walk-throughs".
 *
 * ⭐ "On this phone" comes first and is read from IndexedDB, so it works with NO signal: it is where a tour with
 *   photos still waiting to upload is found again - or with notes, a tag set or a photo edit not yet saved, which
 *   only the capture screen sends (after Finish nothing would). "All walk-throughs" is the server's list, shared across Apex
 *   (his ruling 2026-09-23), each marked with who toured and when - and badged when photos have not uploaded,
 *   because unreadable is unknown, never empty.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, NetworkError } from '../lib/api';
import { getTourQueue, type TourCounts, type TourRecord } from '../lib/tourQueue';
import { counterText, countsOf, unsyncedEdits } from '../lib/tourScreen';
import { displayDate } from '../utils/dates';
import type { TourSummary } from '../types/tours';

const H1 = 'font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]';
const H2 = 'text-[12px] font-semibold uppercase tracking-[.06em] text-gray-500';
const DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
const BADGE = 'rounded-full px-2 py-0.5 text-[11px] font-semibold';

const photos = (n: number): string => `${n} ${n === 1 ? 'photo' : 'photos'}`;

type Local = { record: TourRecord; counts: TourCounts; unsynced: number };

export default function Tours() {
  const [waiting, setWaiting] = useState<Local[] | null>(null);
  const [localIds, setLocalIds] = useState<Set<string>>(new Set());
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [tours, setTours] = useState<TourSummary[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const queue = await getTourQueue();
        const records = await queue.listTours();
        // ONE read per tour: the counts and the unsent edits both come from the same list of its photos.
        const local = await Promise.all(
          records.map(async (record): Promise<Local> => {
            const captures = await queue.listCaptures(record.tourId);
            return { record, counts: countsOf(captures), unsynced: unsyncedEdits(record, captures) };
          }),
        );
        if (!live) return;
        setLocalIds(new Set(records.map((r) => r.tourId)));
        setWaiting(local.filter((t) => t.counts.outstanding > 0 || t.unsynced > 0));
      } catch (err) {
        if (live) setPhoneError(err instanceof Error ? err.message : "This phone's storage could not be read.");
      }
    })();
    api
      .listTours({ limit: 100 })
      .then((r) => {
        if (live) setTours(r.tours);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setListError(
          err instanceof NetworkError
            ? 'No signal - the list of every walk-through needs signal. Anything waiting on this phone is above.'
            : err instanceof Error && err.message
              ? err.message
              : 'The walk-throughs could not be listed.',
        );
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className={H1}>Walk-throughs</h1>
            <p className="mt-1 text-[13px] text-gray-500">
              Every walk-through at Apex, with who toured and when. Its photos stay private to it until you add them
              to the property.
            </p>
          </div>
          <Link
            to="/tours/new"
            className="inline-flex h-10 items-center rounded-lg bg-primary-500 px-4 text-[13.5px] font-semibold text-white hover:bg-primary-600"
          >
            Start a walk-through
          </Link>
        </div>

        <section aria-label="On this phone" className="space-y-2">
          <h2 className={H2}>On this phone</h2>
          {phoneError ? (
            <p className="text-[13px] text-red-700">{phoneError}</p>
          ) : waiting === null ? (
            <p className="text-[13px] text-gray-500">Reading this phone</p>
          ) : waiting.length === 0 ? (
            <p className="text-[13px] text-gray-600">Nothing on this phone is waiting to upload.</p>
          ) : (
            <ul className="space-y-2">
              {waiting.map(({ record, counts, unsynced }) => (
                <li key={record.tourId}>
                  <Link to={`/tour/${record.tourId}`} className="block rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <span className="block text-[14px] font-semibold text-gray-900">{record.propertyLabel}</span>
                    <span className="block text-[12px] text-gray-600">{displayDate(record.touredAt, DAY)}</span>
                    <span
                      className={`block text-[12.5px] ${counts.outstanding > 0 ? 'font-semibold text-amber-800' : 'text-gray-700'}`}
                    >
                      {counterText(counts)}
                    </span>
                    {unsynced > 0 && (
                      <span className="block text-[12.5px] font-semibold text-amber-800">Notes or edits not yet saved</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-label="All walk-throughs" className="space-y-2">
          <h2 className={H2}>All walk-throughs</h2>
          {listError ? (
            <p className="text-[13px] text-gray-700">{listError}</p>
          ) : tours === null ? (
            <p className="text-[13px] text-gray-500">Loading the walk-throughs</p>
          ) : tours.length === 0 ? (
            <p className="text-[13px] text-gray-600">No walk-throughs yet.</p>
          ) : (
            <ul className="space-y-2">
              {tours.map((t) => {
                const where = [t.property?.address, t.property?.city].filter(Boolean).join(', ') || 'Property record unreadable';
                const who = t.toured_by ? (t.toured_by.full_name ?? 'Someone at Apex') : 'Nobody recorded';
                const missing = Math.max(0, t.captured_count - t.uploaded_count);
                const counts = [photos(t.captured_count)];
                if (t.promoted_count) counts.push(`${t.promoted_count} added to property photos`);
                const resume = t.status === 'capturing' && localIds.has(t.id);
                return (
                  <li key={t.id}>
                    <Link
                      to={resume ? `/tour/${t.id}` : `/tour/${t.id}/report`}
                      className="block rounded-xl border border-gray-200 bg-white p-3 hover:border-gray-400"
                    >
                      <span className="block text-[14px] font-semibold text-gray-900">{where}</span>
                      <span className="block text-[12px] text-gray-600">
                        Toured by {who} · {displayDate(t.toured_at, DAY)}
                      </span>
                      <span className="block text-[12.5px] text-gray-800">{counts.join(' · ')}</span>
                      <span className="mt-1 flex flex-wrap gap-1.5">
                        <span className={`${BADGE} ${t.status === 'complete' ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
                          {t.status === 'complete' ? 'Complete' : 'In progress'}
                        </span>
                        {missing > 0 && <span className={`${BADGE} bg-amber-100 text-amber-900`}>{missing} not yet uploaded</span>}
                        {t.property?.is_deleted && <span className={`${BADGE} bg-gray-200 text-gray-700`}>Record deleted</span>}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </Layout>
  );
}
